import { Component, ElementRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  ConnectedPositioningStrategy,
  HorizontalAlignment,
  IgxGridComponent,
  OverlaySettings,
  PositionSettings,
  VerticalAlignment,
} from '@infragistics/igniteui-angular';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import {
  selectInitialSupplementalPremiumColumns,
  selectInitialSupplementalRowStates,
} from 'src/app/company/grid-configuration/data-access/grid-config.selectors';
import { Premiumreportservice } from 'src/app/company/premiumreport/data-access/services/premiumreportservice';
import { ToastService } from 'src/app/shared/toast.service';
import { CompanyService } from '../../../company.service';
import * as premiumReportActions from '../../data-access/premium-report.action';
import {
  columnsSelector,
  currentGridDataSelector,
  rowsSelector,
  userDefinedPremiumColumnSelector,
  userDefinedPremiumDataSelector,
} from '../../data-access/premium-report.selectors';
import { IgxadaptorComponent } from '../../core/igxadaptor/igxadaptor.component';

@Component({
    selector: 'app-supplemental-premiums',
    templateUrl: './supplemental-premiums.component.html',
    styleUrls: ['./supplemental-premiums.component.scss'],
    standalone: false
})
export class SupplementalPremiumsComponent
  extends IgxadaptorComponent
  implements OnInit
{
  @ViewChild('supplementalPremiumsGrid', {
    read: IgxGridComponent,
    static: true,
  })
  currentPremiumsGrid: IgxGridComponent;
  @ViewChild('gridContainer', { static: true }) gridContainer: ElementRef;

  // @Input() public premiumReportState: PremiumReportState;
  @Input() isFullscreen;
   @Input()  isReadOnly;
  public directPremium$ = this.store.select(currentGridDataSelector('D'));
  public gridConfiguration$ = this.store.select(
    selectInitialSupplementalRowStates
  );

  initialData: any;
  public supplementalPremium$ = this.store.select(currentGridDataSelector('S'));
  public userdefined_premium$ = this.store.select(
    userDefinedPremiumDataSelector('D')
  );
  public userdefined_premium_columns$ = this.store.select(
    userDefinedPremiumColumnSelector
  );
  public userdefined_premium_supplemental = this.store.select(
    userDefinedPremiumDataSelector('S')
  );
  public columns$ = combineLatest([
    this.store.select(selectInitialSupplementalPremiumColumns),
    this.store.select(columnsSelector('S')),
  ]).pipe(
    map(([premiumColumns, columns]) => {
      const newModifiedColumns =
        this.premiumReportService.updateColumnVisibility(
          columns,
          premiumColumns
        );
      return newModifiedColumns;
    })
  );
  public data$ = this.premiumReportService.getVisibleData$(
    this.supplementalPremium$,
    this.gridConfiguration$
  );
  public rows$ = this.store.select(rowsSelector);
  public rows: any[];
  public userdefined_row_code = 'UD';
  public supplemental_row_code = 'SP';
  public premium_type: 'S' | 'D' = 'S';
  isLocked: boolean;
  isReadOnlyAdmin: boolean;
private positionSettings: PositionSettings = {
    horizontalStartPoint: HorizontalAlignment.Left,
    verticalStartPoint: VerticalAlignment.Top,
    horizontalDirection: HorizontalAlignment.Left,
    verticalDirection: VerticalAlignment.Top,
  };

  private strategy = new ConnectedPositioningStrategy(this.positionSettings);

  public settings: OverlaySettings = {
    closeOnOutsideClick: false,
    modal: false,
    positionStrategy: this.strategy,
  };

  constructor(
    private companyService: CompanyService,
    public toastService: ToastService,
    private appService: AppService,
    public premiumReportService: Premiumreportservice,
    public store: Store
  ) {
    super(premiumReportService, store);
  }

  ngOnInit(): void {
    this.isLocked = this.companyService.isEditLocked;
    this.isReadOnlyAdmin = this.appService.isReadonlyAdmin;
    this.isReadOnly = this.isLocked || this.isReadOnlyAdmin;

    this.gridClearSelection();
    this.columns$.subscribe((col) => {
      this.columns = col;
    });
    this.rows$.subscribe((rows) => {
      this.rows = rows;
    });
    this.data$.pipe(take(1)).subscribe((intial) => {
      this.initialData = intial;
    });
    this.data$.subscribe((newModifiedRows) => {
      this.currentPremiumsGrid.data = newModifiedRows;
    });
    this.updateTooltipOutlet();
  }

ngOnChanges(changes: SimpleChanges): void {
    if (changes['isFullscreen']) {
      this.updateTooltipOutlet();
    }
  }

  private updateTooltipOutlet() {
    if (this.isFullscreen && this.gridContainer) {
      this.settings.outlet = this.gridContainer.nativeElement;
    } else {
      this.settings.outlet = undefined;
    }
  }

  public commit() {
    this.currentPremiumsGrid.transactions.commit(this.currentPremiumsGrid.data);
    this.store.dispatch(
      premiumReportActions.setGridPremiumData({
        grid: 'S',
        premiumData: this.currentPremiumsGrid.data,
      })
    );
  }

 







  public onCellSelect(data: any) {
    this.columns$
      .pipe(
        take(1),
        withLatestFrom(
          this.supplementalPremium$,
          this.userdefined_premium_supplemental,
          this.userdefined_premium_columns$
        )
      )
      .subscribe(
        ([
          columns,
          supplemental_premium,
          userdefined_premium_supplemental,
          userdefined_premium_supplemental_columns,
        ]) => {
          this.onInputFocus(data);
          const rowIndex = data.cell.row.index + 1;
          const columnIndex = data.cell.column.index;
          //Fetching cell name
          this.store.dispatch(
            premiumReportActions.setSelectedCellName({
              name: this.getColName(columnIndex - 1, rowIndex),
            })
          );
          //Fetching cell formula
          const columnInfo = columns.filter(
            (p) => p.columnNumber == columnIndex
          );

          if (columnInfo?.length) {
            let formula = this.premiumReportService.getFormula(
              columnInfo[0],
              rowIndex,
              false,
              false,
              columns,
              supplemental_premium,
              userdefined_premium_supplemental,
              userdefined_premium_supplemental_columns,
              'S'
            );
            this.store.dispatch(
              premiumReportActions.setSelectedCellFormula({
                formula: formula,
              })
            );
          }
          //Avoid showing formula for user defined and supplemental premium for the last column
          this.avoidFormulaLastColumn(data, columns, columnInfo);

          this.prevGridCellValue = '';
        }
      );
  }

private getSupplementalValue(supplemental_premium: any, state_code: any): number {
  //Get total value from last cell
  if (!supplemental_premium) return 0;
  const match = Object.values(supplemental_premium).find(
    (item: any) => item?.[0] === state_code
  );
  if (match && typeof match === 'object') {
    const values = Object.values(match);
    return values.length > 0 ? Number(values[values.length - 1]) : 0;
  }
  return 0;
}

getTooltipValue(context: any): any {
  if (!context) return null;

  const rowData = context._row?._data || {};
  const dataValues = Object.values(rowData);
  const state_total = dataValues.length > 0 ? Number(dataValues[dataValues.length - 1]) : 0;
  const state_code = rowData['0'];
  let state = rowData['1'];

  if (state_code == 61) {
    state = 'for all states';
  } else if (state_code == 72) {
    state = '';
  } else {
    state = `for ${state}`;
  }

  let supplementalValue = 0;
  let userDefinedSupplementalValue = 0;

  this.supplementalPremium$
    .pipe(
      take(1),
      withLatestFrom(this.userdefined_premium_supplemental)
    )
    .subscribe(([supplemental_premium, userdefined_premium_supplemental]) => {
      supplementalValue = this.getSupplementalValue(supplemental_premium, state_code);
      userDefinedSupplementalValue = this.getSupplementalValue(userdefined_premium_supplemental, state_code);
    });
  supplementalValue -= userDefinedSupplementalValue;

  return {
    state,
    supplemental: supplementalValue,
    userDefinedSupplemental: userDefinedSupplementalValue,
    total: state_total,
  };
}



}
